const WPML_NS_DEFAULT = 'http://www.dji.com/wpmz/1.0.6'

const findDirectChildByTagSuffix = function (parent, suffix) {
  const safeSuffix = String(suffix || '').trim()
  if (!parent || !safeSuffix) return null
  return Array.from(parent.children || []).find((child) => {
    const tag = String(child.tagName || '')
    return tag === safeSuffix || tag.endsWith(':' + safeSuffix)
  }) || null
}

const findFirstChildByTagSuffix = function (parent, suffix) {
  return findDirectChildByTagSuffix(parent, suffix)
}

const createWpmlElement = function (doc, tagLocalName, namespaceUri) {
  if (!doc) return null
  const local = String(tagLocalName || '').trim()
  if (!local) return null
  const ns = String(namespaceUri || '').trim() || WPML_NS_DEFAULT
  try {
    return doc.createElementNS(ns, 'wpml:' + local)
  } catch (_error) {
    return doc.createElement('wpml:' + local)
  }
}

const getDirectElementChildren = function (parentEl) {
  return Array.from((parentEl && parentEl.children) || []).filter((node) => {
    return !!node && node.nodeType === 1
  })
}

const tagMatchesLocalName = function (tagName, localName) {
  const tag = String(tagName || '')
  const local = String(localName || '')
  if (!tag || !local) return false
  return tag === local || tag.endsWith(':' + local)
}

const getDirectChildrenByLocalName = function (parentEl, localName) {
  return getDirectElementChildren(parentEl).filter((node) => {
    return tagMatchesLocalName(node.tagName, localName)
  })
}

const getNthDirectChildByTagName = function (parentEl, tagName, occurrence) {
  const occ = Number.isFinite(Number(occurrence)) ? Number(occurrence) : 0
  const children = getDirectElementChildren(parentEl)
  let seen = 0
  for (let i = 0; i < children.length; i++) {
    const child = children[i]
    if (String(child.tagName || '') !== String(tagName || '')) continue
    if (seen === occ) return child
    seen += 1
  }
  return null
}

const copyXmlLeafValuesBySkeleton = function (sourceEl, targetEl) {
  if (!targetEl) return
  const targetChildren = getDirectElementChildren(targetEl)
  if (!targetChildren.length) {
    if (sourceEl) {
      targetEl.textContent = String(sourceEl.textContent || '')
    }
    return
  }
  const seenByTag = {}
  targetChildren.forEach((targetChild) => {
    const tagName = String(targetChild.tagName || '')
    const occ = seenByTag[tagName] || 0
    seenByTag[tagName] = occ + 1
    const sourceChild = sourceEl ? getNthDirectChildByTagName(sourceEl, tagName, occ) : null
    copyXmlLeafValuesBySkeleton(sourceChild, targetChild)
  })
}

const parseCoordTripletFallback = function (coordText) {
  const parts = String(coordText || '').split(',').map((part) => String(part || '').trim())
  if (parts.length < 2) return null
  const lng = parseFloat(parts[0])
  const lat = parseFloat(parts[1])
  const alt = parts.length > 2 ? parseFloat(parts[2]) : null
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return { lat: lat, lng: lng, alt: Number.isFinite(alt) ? alt : null }
}

const parsePlacemarkCoordinates = function (placemarkEl, parser) {
  if (!placemarkEl) return null
  const pointEl = placemarkEl.getElementsByTagName('Point')[0]
  if (!pointEl) return null
  const coordEl = pointEl.getElementsByTagName('coordinates')[0]
  if (!coordEl) return null
  const raw = coordEl.textContent
  if (typeof parser === 'function') {
    const parsed = parser(raw)
    if (parsed && Number.isFinite(Number(parsed.lat)) && Number.isFinite(Number(parsed.lng))) {
      return parsed
    }
  }
  return parseCoordTripletFallback(raw)
}

const setPlacemarkCoordinates = function (placemarkEl, coord) {
  if (!placemarkEl || !coord) return false
  const pointEl = placemarkEl.getElementsByTagName('Point')[0]
  if (!pointEl) return false
  const coordEl = pointEl.getElementsByTagName('coordinates')[0]
  if (!coordEl) return false
  const lat = Number(coord.lat)
  const lng = Number(coord.lng)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false
  const altRaw = coord.alt
  const hasAlt = altRaw !== null && altRaw !== undefined && String(altRaw).trim() !== ''
  const alt = hasAlt ? Number(altRaw) : NaN
  coordEl.textContent = Number.isFinite(alt)
    ? (String(lng) + ',' + String(lat) + ',' + String(alt))
    : (String(lng) + ',' + String(lat))
  return true
}

const fnIsWhitespaceOnlyTextNode = function (node) {
  return !!node && node.nodeType === 3 && !String(node.nodeValue || '').trim()
}

const fnPrettyFormatXmlElement = function (elementNode, indentText, depth) {
  if (!elementNode || elementNode.nodeType !== 1) return
  const childNodes = Array.from(elementNode.childNodes || [])
  const hasElementChildren = childNodes.some((node) => node && node.nodeType === 1)
  const hasMeaningfulTextChildren = childNodes.some((node) => {
    return !!node && node.nodeType === 3 && String(node.nodeValue || '').trim()
  })
  if (!hasElementChildren || hasMeaningfulTextChildren) return
  childNodes.forEach((node) => {
    if (fnIsWhitespaceOnlyTextNode(node) && node.parentNode === elementNode) {
      elementNode.removeChild(node)
    }
  })
  const formatTargets = Array.from(elementNode.childNodes || []).filter((node) => {
    return !!node && (node.nodeType === 1 || node.nodeType === 7 || node.nodeType === 8)
  })
  formatTargets.forEach((node) => {
    const indentNode = elementNode.ownerDocument.createTextNode('\n' + indentText.repeat(depth + 1))
    elementNode.insertBefore(indentNode, node)
    if (node.nodeType === 1) {
      fnPrettyFormatXmlElement(node, indentText, depth + 1)
    }
  })
  if (formatTargets.length) {
    elementNode.appendChild(elementNode.ownerDocument.createTextNode('\n' + indentText.repeat(depth)))
  }
}

const serializeXmlDocumentPretty = function (xmlDoc, options) {
  if (!xmlDoc) return ''
  const serializer = new XMLSerializer()
  if (!xmlDoc.cloneNode) return serializer.serializeToString(xmlDoc)
  const indentText = String(options && options.indent || '  ')
  const clonedDoc = xmlDoc.cloneNode(true)
  const rootEl = clonedDoc.documentElement || null
  if (rootEl) {
    fnPrettyFormatXmlElement(rootEl, indentText, 0)
  }
  return serializer.serializeToString(clonedDoc)
}

export {
  WPML_NS_DEFAULT,
  findDirectChildByTagSuffix,
  findFirstChildByTagSuffix,
  createWpmlElement,
  getDirectElementChildren,
  tagMatchesLocalName,
  getDirectChildrenByLocalName,
  getNthDirectChildByTagName,
  copyXmlLeafValuesBySkeleton,
  parsePlacemarkCoordinates,
  setPlacemarkCoordinates,
  serializeXmlDocumentPretty
}
